import Papa from "papaparse";

/**
 * Handler for NJTSS file format
 * Supports both single sheet and multi-sheet formats
 * Multi-sheet format: Fall Data Entry, Winter Data Entry, Spring Data Entry
 */
export class NJTSSHandler {
  
  /**
   * Check if file is NJTSS format
   */
  static isNJTSSFile(csvData: string): boolean {
    return csvData.includes('District_Name');
  }

  /**
   * Check if Excel workbook has multiple NJTSS Data Entry sheets
   * Only checks for specific NJTSS sheet names: Fall/Winter/Spring Data Entry
   */
  static hasMultipleDataEntrySheets(sheetNames: string[]): boolean {
    const dataEntrySheets = ['Fall Data Entry', 'Winter Data Entry', 'Spring Data Entry'];
    return sheetNames.some(name => dataEntrySheets.includes(name));
  }

  /**
   * Get all NJTSS Data Entry sheet names from workbook
   * Returns only Fall/Winter/Spring Data Entry sheets
   */
  static getDataEntrySheetNames(sheetNames: string[]): string[] {
    const dataEntrySheets = ['Fall Data Entry', 'Winter Data Entry', 'Spring Data Entry'];
    return sheetNames.filter(name => dataEntrySheets.includes(name));
  }

  /**
   * Process NJTSS CSV data
   */
  static processNJTSSData(csvDataArray: string[]): string[] {
    const csvRowStrArray: string[] = [];
    let isStringFound: boolean = false;

    const studentIDColumn: string[] = [
      'ID',
      'Student ID',
      'Student ID#',
      'student_id',
      'StudentId',
      'Student_ID',
      'first_name',
      'last_name',
      'First',
      'Last'
    ];

    for (let i = 0; i < csvDataArray.length; i++) {
      const csvRowStr = csvDataArray[i];
      
      // Skip blank rows until header found
      if (!studentIDColumn.some((sid) => csvRowStr.includes(sid)) && !isStringFound) {
        continue;
      }
      
      isStringFound = true;
      const csvRowSubStringArray = csvRowStr.split(',');
      const isArrayBlank = csvRowSubStringArray.every((data) => data.trim() === '');

      if (!isArrayBlank) {
        csvRowStrArray.push(csvRowStr);
      }
    }

    return csvRowStrArray;
  }

  /**
   * Parse NJTSS data using Papa Parse
   */
  static parseNJTSSData(
    csvData: string,
    updateDataCallback: (result: any, file: File) => void
  ): void {
    Papa.parse(csvData, {
      complete: updateDataCallback,
      header: true,
      dynamicTyping: true,
      trimHeaders: true,
      skipEmptyLines: true,
      transform(value: string, field: string | number): any {
        return value.trim();
      },
      transformHeader(header: string): any {
        return header.trim();
      },
    });
  }

  /**
   * Process NJTSS sheet data for multi-sheet files
   */
  static processNJTSSSheetData(
    csvData: string,
    workSheetName: string
  ): { processedData: string; period: string; year: string; grade: string } {
    const csvDataArray = csvData.split('\n');

    const yearGradeArray = csvDataArray?.slice(0, 1)[0].split(',');
    const year = yearGradeArray[2];
    const grade = yearGradeArray[3];
    // Extract period from sheet name (Fall, Winter, Spring)
    let period = '';
    if (workSheetName.includes('Fall')) period = 'Fall';
    else if (workSheetName.includes('Winter')) period = 'Winter';
    else if (workSheetName.includes('Spring')) period = 'Spring';

    const csvRowStrArray: string[] = [];
    let isStringFound: boolean = false;

    const studentIDColumn: string[] = [
      'ID',
      'Student ID',
      'Student ID#',
      'student_id',
      'StudentId',
      'Student_ID',
      'first_name',
      'last_name',
      'First',
      'Last'
    ];

    for (let i = 0; i < csvDataArray.length; i++) {
      const csvRowStr = csvDataArray[i];
      
      // Skip blank rows until header found
      if (!studentIDColumn.some((sid) => csvRowStr.includes(sid)) && !isStringFound) {
        continue;
      }
      
      isStringFound = true;
      const csvRowSubStringArray = csvRowStr.split(',');
      const isArrayBlank = csvRowSubStringArray.every((data) => data.trim() === '');

      if (!isArrayBlank) {
        csvRowStrArray.push(csvRowStr);
      }
    }

    const processedData = csvRowStrArray.join('\n');
    return { processedData, period, year, grade };
  }

  /**
   * Parse NJTSS multi-sheet data using Papa Parse
   */
  static parseNJTSSMultiSheetData(
    csvData: string,
    updateMultipleDataCallback: (
      result: any,
      file: File | undefined,
      workSheetName: string,
      sheetIndex: number
    ) => void,
    workSheetName: string,
    sheetIndex: number
  ): void {
    Papa.parse(csvData, {
      complete: (results, file) => {
        updateMultipleDataCallback(results, file, workSheetName, sheetIndex);
      },
      header: true,
      dynamicTyping: true,
      trimHeaders: true,
      skipEmptyLines: true,
      transform(value: string, field: string | number): any {
        return value.trim();
      },
      transformHeader(header: string): any {
        return header.trim();
      },
    });
  }
}
